import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowSquareOut,
  WhatsappLogo,
  Lightning,
  ChatCircle,
  ShoppingCart,
  ChartLineUp,
  Robot,
  Checks
} from '@phosphor-icons/react';
import { CHATNEX_URL } from '../data/chatnex';

const highlights = [
  { icon: ChatCircle, label: 'Live inbox' },
  { icon: Robot, label: 'AI chatbots' },
  { icon: Lightning, label: 'Bulk campaigns' },
  { icon: ShoppingCart, label: 'Ecommerce & UPI' },
  { icon: ChartLineUp, label: 'Analytics' },
  { icon: Checks, label: 'Green tick setup' }
];

const chatnexSlides = [
  { src: '/products/chatnex-inbox.png', alt: 'ChatNex live WhatsApp inbox' },
  { src: '/products/chatnex-contacts.png', alt: 'ChatNex contacts list' },
  { src: '/products/chatnex-ai.png', alt: 'ChatNex AI chatbot integrations' }
];

const ChatNexFadeSlide = () => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setActive((n) => (n + 1) % chatnexSlides.length);
    }, 3800);
    return () => window.clearInterval(id);
  }, [active]);

  return (
    <div className="relative w-full aspect-[16/10] bg-[#f7faf9] overflow-hidden">
      {chatnexSlides.map((item, index) => (
        <img
          key={item.src}
          src={item.src}
          alt={index === active ? item.alt : ''}
          className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ease-out ${
            index === active ? 'opacity-100 z-[1]' : 'opacity-0 z-0'
          }`}
        />
      ))}
      <div className="absolute inset-x-0 bottom-0 z-[2] h-14 bg-gradient-to-t from-black/45 to-transparent pointer-events-none" />
      <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 z-[3] flex items-center justify-center gap-2">
        {chatnexSlides.map((item, index) => (
          <button
            key={item.src}
            type="button"
            aria-label={`Show ChatNex screenshot ${index + 1}`}
            onClick={() => setActive(index)}
            className={`h-2 rounded-full smooth-transition ${
              index === active ? 'w-6 bg-white' : 'w-2 bg-white/55 hover:bg-white/85'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const Products = () => {
  return (
    <section id="products" className="pt-0 pb-12 md:pb-16 bg-white dark:bg-dark-900">
      <div className="section-max-width px-4 md:px-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-[minmax(0,1.15fr)_minmax(280px,0.85fr)] rounded-2xl sm:rounded-3xl overflow-hidden border border-gray-100 dark:border-gray-700 bg-white dark:bg-dark-800 shadow-large">
          <div className="p-4 sm:p-6 lg:p-10 flex flex-col justify-center bg-gradient-to-br from-white to-emerald-50/70 dark:from-dark-800 dark:to-dark-900">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 text-[11px] sm:text-xs font-bold uppercase tracking-wider w-fit mb-4">
              <WhatsappLogo className="w-4 h-4" weight="fill" />
              Official WhatsApp Business API
            </div>

            <h2 className="text-display text-[1.45rem] sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-3">
              Sell more on WhatsApp with{' '}
              <span className="whitespace-nowrap">
                Chat<span className="text-red-600 dark:text-red-500">Nex</span>
              </span>
            </h2>

            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 leading-relaxed mb-5 max-w-lg">
              Official Meta API, AI chatbots, bulk campaigns, live inbox, catalogs and payments — for sales, support and ecommerce.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
              {highlights.map((item) => {
                const Icon = item.icon;
                return (
                  <span
                    key={item.label}
                    className="inline-flex items-center gap-2 min-h-[40px] px-2.5 py-2 rounded-xl text-[11px] sm:text-xs font-semibold bg-white dark:bg-dark-700 text-gray-700 dark:text-gray-200 border border-gray-100 dark:border-white/10 shadow-soft"
                  >
                    <Icon className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" weight="fill" />
                    <span className="leading-tight">{item.label}</span>
                  </span>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row gap-2.5">
              <a
                href={CHATNEX_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-white inline-flex items-center justify-center gap-2 px-6 py-3 w-full sm:w-auto"
              >
                Open ChatNex
                <ArrowSquareOut className="w-4 h-4" weight="bold" />
              </a>
              <Link
                to="/services/whatsapp-business-automation"
                className="btn-secondary inline-flex items-center justify-center px-6 py-3 w-full sm:w-auto"
              >
                See all features
              </Link>
            </div>
          </div>

          <div className="flex items-center justify-center bg-[#eef3f1] dark:bg-dark-950 p-3 sm:p-6">
            <div className="w-full rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-medium bg-white">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-dark-800 border-b border-gray-100 dark:border-white/10">
                <span className="flex gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-400" />
                  <span className="w-2 h-2 rounded-full bg-amber-400" />
                  <span className="w-2 h-2 rounded-full bg-green-400" />
                </span>
                <span className="text-[10px] font-semibold text-gray-400 truncate">chatnex.in</span>
              </div>
              <ChatNexFadeSlide />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Products;
